/**
 * API envelope types and camelCase UI state for the server-backed user preference store
 * (`GET/PUT /me/preference/{preference_key}`, `GET /cases/{case_id}/preference/medchron_export`).
 * Today the only preference key is `medchron_export` (MedChron export appearance/layout
 * defaults); the envelope is written to be extended if more keys are added later.
 */
import type {
  ChronologySortField,
  ChronologyViewType,
  SortDirection,
} from "@/components/MedicalChronology/Visits/types";

export const USER_PREFERENCE_KEY_MEDCHRON_EXPORT = "medchron_export" as const;

export type VisitHeadingTextColor = "black" | "blue";

/**
 * `chronology_view_group_by` on the wire: `null` means "no grouping" and maps to
 * `ChronologyViewType.DATE` on the client — there is no `"dateOnly"` value on the API side.
 */
export type MedChronExportGroupByApi =
  | ChronologyViewType.PROVIDER
  | ChronologyViewType.VISIT_TYPE
  | ChronologyViewType.FILE
  | null;

export type MedChronExportNameFormatApi = "last_first" | "first_only" | "last_only" | null;

export interface MedChronExportViewCustomizationApi {
  date_format: string;
  visit_heading_text_color: VisitHeadingTextColor;
  visit_heading_underline: boolean;
  visit_heading_date_location_separator: string;
  chronology_view_group_by: MedChronExportGroupByApi;
  chronology_sort_field: ChronologySortField;
  chronology_sort_direction: SortDirection;
  name_format: MedChronExportNameFormatApi;
}

export interface MedChronExportDataCustomizationApi {
  collapse_therapy_visits: boolean;
}

export interface MedChronExportCustomizationOptionsApi {
  data_customization: MedChronExportDataCustomizationApi;
  view_customization: MedChronExportViewCustomizationApi;
}

export interface MedChronExportPreferenceValue {
  customization_options: MedChronExportCustomizationOptionsApi;
}

/**
 * Shared by both GET routes and the PUT response. The server always returns a complete
 * value — whether a row was ever saved or these are server defaults — so the FE must not
 * try to detect first-time/empty state from this shape, and must not keep a second
 * client-side fallback-default object.
 */
export interface UserPreferenceResponse {
  preference_key: typeof USER_PREFERENCE_KEY_MEDCHRON_EXPORT;
  schema_version: number;
  value: MedChronExportPreferenceValue;
}

export interface UserPreferencePutRequest {
  schema_version: number;
  value: MedChronExportPreferenceValue;
}

/** UI state for MedChron export appearance/layout, consumed by the profile page and export dialog. */
export interface MedChronExportUiState {
  schemaVersion: number;
  collapseTherapyVisits: boolean;
  chronologyViewGroupBy: ChronologyViewType;
  chronologySortField: ChronologySortField;
  chronologySortDirection: SortDirection;
  dateFormat: string;
  visitHeadingTextColor: VisitHeadingTextColor;
  visitHeadingUnderline: boolean;
  visitHeadingDateLocationSeparator: string;
}
